import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ITTDSubAdvertiser } from '@feathr/blackbox';
import { ArchiveModalV1, ContextMenu, toast } from '@feathr/components';
import { useAccount, useLocalUrl } from '@feathr/extender/state';
import { getIconForAction, useToggle } from '@feathr/hooks';

interface IProps {
  original: ITTDSubAdvertiser;
}

function AdvertiserOptionsCell({ original }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const account = useAccount();
  const [showArchiveModal, toggleArchiveModal] = useToggle(false);

  async function deleteMessage(): Promise<void> {
    if (!account) {
      return;
    }
    account.set({
      ttd: {
        ...account.get('ttd', {}),
        subadvertisers: (account.get('ttd', { subadvertisers: [] }).subadvertisers || []).filter(
          (subAdv) => subAdv.subadv_id !== original.subadv_id,
        ),
      },
    });
    await account.patchDirty();
    if (account.isErrored) {
      toast(
        t('Encountered an error while deleting advertiser:\n{{errorMessage}}', {
          errorMessage: account.error?.message,
        }),
        {
          type: 'error',
        },
      );
    }
    toast(t('Deleted advertiser.'), { type: 'success' });
    toggleArchiveModal();
  }

  return (
    <>
      <ContextMenu buttonType={'icon'} position={'left-start'}>
        <ContextMenu.Item
          link={localUrl(`settings/account/advertisers/${original.subadv_id}`)}
          prefix={getIconForAction('edit')}
        >
          {t('Edit')}
        </ContextMenu.Item>
        <ContextMenu.Item
          onClick={toggleArchiveModal}
          prefix={getIconForAction('delete')}
          theme={'danger'}
        >
          {t('Delete')}
        </ContextMenu.Item>
      </ContextMenu>
      {showArchiveModal && (
        <ArchiveModalV1
          message={t(
            'Are you sure you want to delete this advertiser? This action cannot be undone.',
          )}
          onClose={toggleArchiveModal}
          onConfirm={deleteMessage}
          t={t}
          title={t('Delete Message')}
        />
      )}
    </>
  );
}

export default observer(AdvertiserOptionsCell);
